import clsx from "clsx";
import React from "react";
import { createUseStyles } from "react-jss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { revoke } from "../../services/session";
import User from "../refresh/tours/User";
import FindScene from "./FindScene";

/**
 * StudioToolbar — the top bar for TouchTour Studio (WP-A).
 *
 * Three regions, matching the mockup:
 *   - left:   title + Options + Embed
 *   - center: camera controls (Fit All / Fit Selected / Tidy Board) driven
 *             through the Canvas API
 *   - right:  Save + Exit + User menu
 *
 * The camera buttons read the Canvas API from `canvasApiRef.current` at click
 * time — the ref is populated by TourCanvas' `onReady` after mount, so we never
 * reach into TourCanvas internals.
 */

const useStyles = createUseStyles(theme => ({
  topbar: {
    "background-color": theme.colorBackground,
    height: 50,
    width: "100%",
    padding: [0, 15],
    display: "flex",
    "flex-direction": "row",
    "align-items": "center",
    color: theme.textColor,
    "box-sizing": "border-box",
    "border-bottom": `1px solid ${theme.colorDarkGrey}`,
    position: "relative",
    overflow: "visible",
  },
  left: {
    display: "flex",
    "align-items": "center",
    height: "100%",
    flex: "1 1 0",
    "min-width": 0,
  },
  center: {
    display: "flex",
    "align-items": "center",
    flex: "0 0 auto",
    position: "relative",
    zIndex: 2,
    overflow: "visible",
  },
  findScene: {
    position: "absolute",
    left: "100%",
    top: "50%",
    transform: "translateY(-50%)",
    "margin-left": 10,
    zIndex: 3,
  },
  right: {
    display: "flex",
    "align-items": "center",
    height: "100%",
    flex: "1 1 0",
    "justify-content": "flex-end",
  },
  title: {
    "font-size": 18,
    "font-weight": 900,
    margin: 0,
    "margin-right": theme.spacing3,
    "white-space": "nowrap",
    "@media (max-width: 900px)": {
      display: "none",
    },
  },
  button: {
    display: "flex",
    "align-items": "center",
    height: "100%",
    padding: [0, 10],
    transition: "background-color 0.2s ease",
    cursor: "pointer",
    "white-space": "nowrap",

    "&:hover": {
      "background-color": "#313131",
    },

    "& svg": {
      "font-size": 18,
      "@media (min-width: 750px)": {
        "margin-right": 10,
      },
    },
  },
  disabled: {
    cursor: "not-allowed",
    color: theme.colorGrey,
    "&:hover": {
      "background-color": "transparent",
    },
  },
  segment: {
    display: "flex",
    "align-items": "center",
    border: `1px solid ${theme.colorDarkGrey}`,
    "border-radius": theme.borderRadius2,
    overflow: "hidden",
    height: 32,
  },
  segmentButton: {
    display: "flex",
    "align-items": "center",
    height: "100%",
    padding: [0, 14],
    "font-size": 13,
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "border-left": `1px solid ${theme.colorDarkGrey}`,
    "white-space": "nowrap",

    "&:first-child": {
      "border-left": "none",
    },

    "&:hover": {
      "background-color": "#313131",
    },

    "& svg": {
      "font-size": 14,
      "margin-right": 8,
    },
  },
}));

const StudioToolbar = ({
  canvasApiRef,
  activeImage = null,
  selectedIds = null,
  images = null,
  onSelectImage,
  onTidyBoard,
  onUndoTidyBoard = null,
  onTourSettings,
  onEmbedTour,
  onSaveTour,
  onExit,
  dirty,
}) => {
  const classes = useStyles();

  const api = () => canvasApiRef && canvasApiRef.current;

  const handleFitAll = () => {
    const a = api();
    if (a) a.fitToView({ all: true });
  };

  // Prefer the marquee multi-selection (WP-L) when it has nodes; otherwise fall
  // back to the single active node so the button keeps working as before.
  const marqueeIds = Array.isArray(selectedIds) ? selectedIds : [];
  const fitIds =
    marqueeIds.length > 0
      ? marqueeIds
      : activeImage !== null && activeImage !== undefined
        ? [activeImage]
        : [];
  const hasSelection = fitIds.length > 0;
  const handleFitSelected = () => {
    if (!hasSelection) return;
    const a = api();
    if (a) a.fitToImages(fitIds);
  };

  // Wired by the parent (NodeEditor) to tidyBoard.js; falls back to a stub if a
  // host hasn't supplied onTidyBoard.
  const handleTidyBoard = () => {
    if (onTidyBoard) {
      onTidyBoard();
      return;
    }
    // eslint-disable-next-line no-alert
    alert("Coming soon");
  };

  return (
    <div className={classes.topbar}>
      <div className={classes.left}>
        <h1 className={classes.title}>TouchTour Studio</h1>
        <div className={classes.button} onClick={onTourSettings}>
          <FontAwesomeIcon icon={["fal", "gear"]} fixedWidth />
          Options
        </div>
        <div className={classes.button} onClick={onEmbedTour}>
          <FontAwesomeIcon icon={["fal", "code"]} fixedWidth />
          Embed
        </div>
      </div>

      <div className={classes.center}>
        <div className={classes.segment}>
          <div className={classes.segmentButton} onClick={handleFitAll}>
            <FontAwesomeIcon icon={["fal", "maximize"]} fixedWidth />
            Fit All
          </div>
          <div
            className={clsx(classes.segmentButton, {
              [classes.disabled]: !hasSelection,
            })}
            onClick={handleFitSelected}
          >
            <FontAwesomeIcon icon={["fal", "object-group"]} fixedWidth />
            Fit Selected
          </div>
          <div className={classes.segmentButton} onClick={handleTidyBoard}>
            <FontAwesomeIcon icon={["fal", "wand-magic-sparkles"]} fixedWidth />
            Tidy Board
          </div>
          {onUndoTidyBoard && (
            <div className={classes.segmentButton} onClick={onUndoTidyBoard}>
              <FontAwesomeIcon icon={["fal", "rotate-left"]} fixedWidth />
              Undo Tidy
            </div>
          )}
        </div>
        <div className={classes.findScene}>
          <FindScene
            canvasApiRef={canvasApiRef}
            images={images}
            onSelectImage={onSelectImage}
          />
        </div>
      </div>

      <div className={classes.right}>
        <div
          className={clsx(classes.button, { [classes.disabled]: !dirty })}
          onClick={onSaveTour}
        >
          <FontAwesomeIcon
            icon={["fal", dirty ? "floppy-disk" : "floppy-disk-circle-xmark"]}
            fixedWidth
          />
          Save
        </div>
        <div className={classes.button} onClick={onExit}>
          <FontAwesomeIcon icon={["fal", "person-running"]} fixedWidth />
          Exit
        </div>
        <User logout={revoke} />
      </div>
    </div>
  );
};

export default StudioToolbar;
